import { useEffect } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity } from 'react-native';
import { useRouter } from 'expo-router';
import type { Href } from 'expo-router';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';

type QaLink = {
  label: string;
  route: Href;
  disabled?: boolean;
  helperText?: string;
};

type QaSection = {
  title: string;
  items: QaLink[];
};

export default function LaunchQaScreen() {
  const router = useRouter();
  const { profile } = useAuth();
  const { theme } = useTheme();

  const isOwnerDemoMode =
    (profile as any)?.owner_demo_mode === true || (profile as any)?.is_owner_demo_mode === true;
  const isScreenshotMode =
    (profile as any)?.screenshot_mode === true || (profile as any)?.is_screenshot_mode === true;
  const shouldHide = !__DEV__ || isOwnerDemoMode || isScreenshotMode;

  // V1: Lock dev routes in production
  useEffect(() => {
    if (shouldHide) {
      router.replace('/');
    }
  }, [router, shouldHide]);

  if (shouldHide) {
    return null;
  }

  const sections: QaSection[] = [
    {
      title: 'Onboarding',
      items: [
        { label: 'Start Onboarding', route: '/(onboarding)/username' },
        { label: 'Sign Up (Invite)', route: '/(auth)/signup' },
      ],
    },
    {
      title: 'Free Core Loop',
      items: [
        { label: 'Record Workout', route: '/(tabs)/record' },
        { label: 'Profile', route: '/(tabs)/profile' },
        { label: 'Leaderboards', route: '/(tabs)/leaderboards' },
        { label: 'Gym Leaderboard', route: '/gym/leaderboard' },
      ],
    },
    {
      title: 'Weekly Challenge / Rewards',
      items: [
        { label: 'Challenges', route: '/(profile)/challenges' },
        { label: 'Progression', route: '/progression' },
        { label: 'Badges', route: '/(tabs)/badges' },
      ],
    },
    {
      title: 'Developer Tools',
      items: [
        { label: 'Settings (Dev Tools)', route: '/settings' },
        { label: 'Join Gym (Code)', route: '/join-gym' },
        {
          label: 'Show Gym Join Code',
          route: '/gym-join-code',
          disabled: !profile?.gym_id,
          helperText: !profile?.gym_id ? 'Set a gym first to view the join code.' : undefined,
        },
      ],
    },
  ];

  return (
    <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
      <View style={[styles.header, { borderBottomColor: theme.colors.border }]}>
        <Text style={[styles.title, { color: theme.colors.accent }]}>Launch QA</Text>
      </View>
      <ScrollView contentContainerStyle={styles.content}>
        {sections
          .filter(section => section.items.length > 0)
          .map(section => (
            <View
              key={section.title}
              style={[styles.section, { backgroundColor: theme.colors.surface }]}
            >
              <Text style={[styles.sectionTitle, { color: theme.colors.text }]}>
                {section.title}
              </Text>
              {section.items.map(item => (
                <View
                  key={typeof item.route === 'string' ? item.route : item.route.pathname}
                >
                  <TouchableOpacity
                    style={[
                      styles.button,
                      {
                        backgroundColor: theme.colors.surfaceVariant,
                        borderColor: theme.colors.border,
                        opacity: item.disabled ? 0.5 : 1,
                      },
                    ]}
                    onPress={() => {
                      if (!item.disabled) {
                        router.push(item.route);
                      }
                    }}
                    disabled={item.disabled}
                  >
                    <Text style={[styles.buttonText, { color: theme.colors.textSecondary }]}>
                      {item.label}
                    </Text>
                  </TouchableOpacity>
                  {item.helperText ? (
                    <Text style={[styles.helperText, { color: theme.colors.textTertiary }]}>
                      {item.helperText}
                    </Text>
                  ) : null}
                </View>
              ))}
            </View>
          ))}
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    paddingTop: 60,
    paddingBottom: 16,
    paddingHorizontal: 20,
    borderBottomWidth: 1,
  },
  title: {
    fontSize: 20,
    fontWeight: '900',
    letterSpacing: 1,
  },
  content: {
    padding: 16,
    paddingBottom: 40,
  },
  section: {
    borderRadius: 2,
    padding: 16,
    marginBottom: 16,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '800',
    marginBottom: 12,
  },
  button: {
    borderWidth: 1,
    paddingVertical: 12,
    paddingHorizontal: 12,
    borderRadius: 2,
    marginBottom: 10,
  },
  buttonText: {
    fontSize: 13,
    fontWeight: '600',
    textAlign: 'center',
  },
  helperText: {
    fontSize: 12,
    marginBottom: 8,
    marginTop: -6,
    textAlign: 'center',
  },
});
